import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Autocomplete,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
} from '@mui/material';
import {
  GoogleMap,
  useJsApiLoader,
  Circle,
  InfoWindow,
  OverlayViewF,
} from '@react-google-maps/api';
import { CalendarTodayOutlined } from '@mui/icons-material';
import { getYears } from '../../../helpers/dateHelpers';
import { getStatePersonalizedV2 } from '../../../helpers/api';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

// 1. Keep your realistic mock facilities here
const MOCK_FACILITIES_DATABASE = {
  lagos: [
    {
      id: 1,
      name: 'Ikeja Industrial Gas Plant',
      lat: 6.6018,
      lng: 3.3515,
      emissionLevel: 88,
      status: 'Critical',
    },
    {
      id: 2,
      name: 'Apapa Port Power Gen Station',
      lat: 6.4449,
      lng: 3.3689,
      emissionLevel: 95,
      status: 'Critical',
    },
    {
      id: 3,
      name: 'Ikorodu Manufacturing Hub',
      lat: 6.6175,
      lng: 3.5097,
      emissionLevel: 62,
      status: 'Warning',
    },
    {
      id: 4,
      name: 'Lekki Phase I Boiler Facility',
      lat: 6.4474,
      lng: 3.4735,
      emissionLevel: 41,
      status: 'Normal',
    },
  ],
  ogun: [
    {
      id: 5,
      name: 'Agbara Industrial Estate Chem-Plant',
      lat: 6.5022,
      lng: 3.1042,
      emissionLevel: 91,
      status: 'Critical',
    },
    {
      id: 6,
      name: 'Sagamu Cement Kiln',
      lat: 6.8322,
      lng: 3.6319,
      emissionLevel: 78,
      status: 'Warning',
    },
    {
      id: 7,
      name: 'Ota Steel Rolling Mill',
      lat: 6.6906,
      lng: 3.2323,
      emissionLevel: 99,
      status: 'Critical',
    },
    {
      id: 8,
      name: 'Abeokuta Food Processing Plant',
      lat: 7.1557,
      lng: 3.3444,
      emissionLevel: 35,
      status: 'Normal',
    },
  ],
};

export default function MapLive() {
  const years = getYears();
  const currentYear = new Date().getFullYear()?.toString();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const initState = {
    stateId: '',
    stateName: '',
    coordinates: {
      latitude: '',
      longitude: '',
    },
    lga: [],
  };

  const [stateList, setStateList] = useState([]);
  const [stateLoading, setStateLoading] = useState(true);
  const [stateSelected, setStateSelected] = useState(initState);

  const [activeMarker, setActiveMarker] = useState(null);
  const [map, setMap] = useState(null);

  // Load Google Maps script WITHOUT API key
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: '',
  });

  // Fetch state list on mount
  useEffect(() => {
    fetchStateList();
  }, []);

  // Update map viewport dynamically using API coordinates
  useEffect(() => {
    if (map && stateSelected?.coordinates?.latitude) {
      const newCenter = {
        lat: parseFloat(stateSelected.coordinates.latitude),
        lng: parseFloat(stateSelected.coordinates.longitude),
      };
      map.panTo(newCenter);
      map.setZoom(stateSelected.stateName?.toLowerCase() === 'ogun' ? 10 : 11);
    }

    if (stateSelected?.stateId) {
      fetchResultList();
    }
  }, [stateSelected, selectedYear, map]);

  const fetchResultList = () => {
    console.log('Fetching compliance list for state ID:', stateSelected?.stateId);
  };

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const getStatusColor = (level) => {
    if (level >= 85) return '#ff1744'; // Red (Critical)
    if (level >= 60) return '#ff9100'; // Orange (Warning)
    return '#00e676'; // Green (Normal)
  };

  const fetchStateList = async () => {
    setStateLoading(true);
    try {
      const res = await getStatePersonalizedV2();
      const { data, status } = res?.result || {};
      if (status?.code === '00' && data?.length > 0) {
        setStateList(data);
        setStateSelected(data[0]); // Default select Lagos
      }
    } catch (error) {
      console.error('Error fetching structural personalized state list:', error);
    } finally {
      setStateLoading(false);
    }
  };

  const getMapCenter = () => {
    if (stateSelected?.coordinates?.latitude && stateSelected?.coordinates?.longitude) {
      return {
        lat: parseFloat(stateSelected.coordinates.latitude),
        lng: parseFloat(stateSelected.coordinates.longitude),
      };
    }
    return { lat: 6.5244, lng: 3.3792 };
  };

  // Get the mock facilities matching the selected state name from the API
  const getActiveFacilities = () => {
    const key = stateSelected?.stateName?.toLowerCase() || 'lagos';
    return MOCK_FACILITIES_DATABASE[key] || [];
  };

  const activeFacilities = getActiveFacilities();

  if (loadError) {
    return <Box sx={{ p: 3 }}>Error loading Google Maps</Box>;
  }

  const renderFilterControls = () => {
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        width={{ xs: '100%', sm: 'auto' }}
        alignItems="center"
      >
        <Select
          value={selectedYear}
          size="small"
          onChange={(e) => setSelectedYear(e.target.value)}
          startAdornment={
            <CalendarTodayOutlined sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          }
          sx={{
            borderRadius: 3,
            minWidth: 120,
            fontWeight: 600,
            bgcolor: 'background.paper',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {years?.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </Select>

        <Autocomplete
          disablePortal
          value={stateSelected?.stateId ? stateSelected : null}
          options={stateList}
          loading={stateLoading}
          getOptionLabel={(option) => option.stateName || ''}
          onChange={(event, newValue) => {
            setStateSelected(newValue || initState);
            setActiveMarker(null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="All States"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {stateLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {params.InputProps.startAdornment}
                  </>
                ),
                sx: {
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <MenuItem {...props} key={option.stateId}>
              <Typography variant="body2">{option.stateName}</Typography>
            </MenuItem>
          )}
          sx={{ width: { xs: '100%', sm: 150 } }}
        />
      </Stack>
    );
  };

  return (
    <Box sx={{ p: 3, height: '100vh', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={2}
        sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Emission Compliance Tracker
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Map center set by API coordinates ({stateSelected?.stateName || 'None'})
          </Typography>
        </Box>
        {renderFilterControls()}
      </Paper>

      <Box sx={{ flexGrow: 1, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
        {isLoaded && !stateLoading ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={getMapCenter()}
            zoom={stateSelected.stateName?.toLowerCase() === 'ogun' ? 10 : 11}
            onLoad={onMapLoad}
          >
            {activeFacilities.map((fac) => {
              const markerColor = getStatusColor(fac.emissionLevel);
              const position = { lat: fac.lat, lng: fac.lng };
              const isCritical = fac.emissionLevel >= 85;

              return (
                <React.Fragment key={fac.id}>
                  {/* Heatmap Area Circle */}
                  <Circle
                    center={position}
                    radius={1200 + fac.emissionLevel * 10}
                    options={{
                      strokeColor: markerColor,
                      strokeOpacity: 0.8,
                      strokeWeight: 1,
                      fillColor: markerColor,
                      fillOpacity: 0.35,
                    }}
                  />

                  {/* HTML Overlay Marker with Blinking Pulse Animation */}
                  <OverlayViewF
                    position={position}
                    mapPaneName="overlayMouseTarget"
                    getPixelPositionOffset={(width, height) => ({
                      x: -(width / 2),
                      y: -(height / 2),
                    })}
                  >
                    <Box
                      onClick={() => setActiveMarker(fac)}
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                      }}
                    >
                      {/* Blinking Aura Ring (only on Critical status) */}
                      {isCritical && (
                        <Box
                          sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            backgroundColor: markerColor,
                            opacity: 0.6,
                            animation: 'pulse-ring 1.5s infinite ease-out',
                            '@keyframes pulse-ring': {
                              '0%': {
                                transform: 'scale(0.5)',
                                opacity: 0.8,
                              },
                              '100%': {
                                transform: 'scale(2.2)',
                                opacity: 0,
                              },
                            },
                          }}
                        />
                      )}

                      {/* Static Center Core Pin */}
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: markerColor,
                          border: '2px solid white',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.2)',
                          },
                        }}
                      />
                    </Box>
                  </OverlayViewF>

                  {/* Info Window pop-up */}
                  {activeMarker?.id === fac.id && (
                    <InfoWindow position={position} onCloseClick={() => setActiveMarker(null)}>
                      <Box sx={{ p: 0.5, minWidth: 150 }}>
                        <Typography variant="subtitle2" color="text.primary" fontWeight="bold">
                          {fac.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Lat: {fac.lat.toFixed(4)}, Lng: {fac.lng.toFixed(4)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Typography variant="body2" color="text.primary">
                            Emission:
                          </Typography>
                          <Chip
                            label={`${fac.emissionLevel}%`}
                            size="small"
                            color={
                              isCritical ? 'error' : fac.emissionLevel >= 60 ? 'warning' : 'success'
                            }
                          />
                        </Box>
                      </Box>
                    </InfoWindow>
                  )}
                </React.Fragment>
              );
            })}
          </GoogleMap>
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Connecting map with API response payload...
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
